import React, { useState, useMemo } from 'react'
import { useQuery } from '@apollo/client/react'
import {
  Grid,  Card, CardMedia, CardContent, Typography, Dialog, DialogContent, IconButton, Box, CircularProgress, TextField, InputAdornment, Link, Alert } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import {
  PublicPageLayout,
  getNextLevelLogoProps,
} from '@/housefolios-components/PublicPages/shared'
import { GET_YOUTUBE_VIDEOS } from '@/housefolios-components/PublicProperty/queries'
import { formatDate } from '@/utils/date'

// Channel ID for Robert's YouTube channel (@robertstewartnextlevel)
const DEFAULT_CHANNEL_ID = 'UCkTuMHdurT_jJaYfgYTGOPQ'

function PublicEducationPage(props) {
  const [selectedVideo, setSelectedVideo] = useState(null)
  const [openDialog, setOpenDialog] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const { logoSrc, logoAlt } = getNextLevelLogoProps(props.marketplace)

  const { loading, error, data } = useQuery(GET_YOUTUBE_VIDEOS, {
    variables: {
      channelId: DEFAULT_CHANNEL_ID, // Pass null to use default in resolver
    },
    skip: false })

  const allVideos = data?.getYouTubeVideos || []

  // Filter videos based on search query (title only)
  const videos = useMemo(() => {
    if (!searchQuery.trim()) {
      return allVideos
    }
    const query = searchQuery.toLowerCase()
    return allVideos.filter((video) =>
      video.title?.toLowerCase().includes(query),
    )
  }, [allVideos, searchQuery])

  const handleVideoClick = (video) => {
    setSelectedVideo(video)
    setOpenDialog(true)
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setSelectedVideo(null)
  }

  // Extract video ID from link if needed
  const getVideoId = (video) => {
    if (video.id) return video.id
    // Extract from link if ID not available
    const match = video.link?.match(/[?&]v=([^&]+)/)
    return match ? match[1] : ''
  }

  const getEmbedUrl = (videoId) => {
    return `https://www.youtube.com/embed/${videoId}?autoplay=1`
  }

  return (
    <PublicPageLayout
      title={
        props.marketplace
          ? `${props.marketplace.name} Education`
          : 'Education'
      }
      description="Learn from our educational videos"
      icon={logoSrc ? undefined : ['fas', 'video']}
      logoSrc={logoSrc}
      logoAlt={logoAlt}
      loading={loading}
      error={error}
      containerProps={{
        direction: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        padding: 5}}
    >
      {loading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px"
          }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Box sx={{
          padding: 3
        }}>
          <Typography color="error">
            Error loading videos: {error.message}
          </Typography>
        </Box>
      )}

      {!loading && !error && videos.length === 0 && (
        <Box sx={{
          padding: 3
        }}>
          <Typography>No videos available at this time.</Typography>
        </Box>
      )}

      {!loading && !error && allVideos.length > 0 && (
        <>
          {/* Info Alert */}
          <Box
            sx={{
              width: '100%',
              maxWidth: 1200,
              marginBottom: 2,
              marginTop: 2}}
          >
            <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
              Showing the most recent 15 videos.{' '}
              <Link
                href="https://www.youtube.com/@robertstewartnextlevel"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  textDecoration: 'underline',
                  fontWeight: 600}}
              >
                View all videos on YouTube
                <OpenInNewIcon sx={{ fontSize: '0.875rem' }} />
              </Link>
            </Alert>
          </Box>

          {/* Search Bar */}
          <Box
            sx={{
              width: '100%',
              maxWidth: 600,
              marginBottom: 4}}
          >
            <TextField
              fullWidth
              placeholder="Search videos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                backgroundColor: 'white',
                borderRadius: 1}}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )}
              }}
            />
          </Box>

          {/* Results count */}
          {searchQuery && (
            <Box sx={{ width: '100%', maxWidth: 1200, marginBottom: 2 }}>
              <Typography variant="body2" sx={{
                color: "text.secondary"
              }}>
                {videos.length === 0
                  ? 'No videos found matching your search'
                  : `Found ${videos.length} video${videos.length !== 1 ? 's' : ''}`}
              </Typography>
            </Box>
          )}

          {/* Videos Grid */}
          {videos.length > 0 ? (
            <Grid
              container
              spacing={4}
              style={{ marginTop: 20, maxWidth: 1200, width: '100%' }}
            >
          {videos.map((video) => {
            const videoId = getVideoId(video)
            const publishDate = video.publishDate
              ? formatDate(video.publishDate, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'})
              : ''

            return (
              <Grid size={{xs: 12, sm: 6, md: 4}} key={video.id || video.link}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 6}}}
                  onClick={() => handleVideoClick(video)}
                >
                  <CardMedia
                    component="img"
                    height="200"
                    image={video.thumbnail}
                    alt={video.title}
                    sx={{
                      objectFit: 'cover',
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 68,
                        height: 48,
                        background:
                          'url("https://www.youtube.com/s/desktop/favicon.ico") no-repeat center',
                        backgroundSize: 'contain',
                        opacity: 0.8}}}
                  />
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{
                        fontSize: '1rem',
                        fontWeight: 600,
                        marginBottom: 1,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'}}
                    >
                      {video.title}
                    </Typography>
                    {publishDate && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          fontSize: '0.875rem'
                        }}>
                        {publishDate}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
            </Grid>
          ) : (
            <Box sx={{
              padding: 3
            }}>
              <Typography>No videos match your search criteria.</Typography>
            </Box>
          )}
        </>
      )}

      {/* Video Modal Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              backgroundColor: '#000'}}
        }}
      >
        <DialogContent sx={{ padding: 0, position: 'relative' }}>
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'white',
              zIndex: 1,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.7)'}}}
          >
            <CloseIcon />
          </IconButton>
          {selectedVideo && (
            <Box
              sx={{
                position: 'relative',
                paddingBottom: '56.25%', // 16:9 aspect ratio
                height: 0,
                overflow: 'hidden'}}
            >
              <iframe
                src={getEmbedUrl(getVideoId(selectedVideo))}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%'}}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </PublicPageLayout>
  );
}

export default PublicEducationPage
